import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { LOCALES } from '@lensmira/i18n'
import { MESSAGE_CHANNELS } from '@lensmira/domain'

import { useFieldChecks, errorId, type FieldCheck } from '../forms/field-checks.js'
import { Ltr, useTranslation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { chaseChannelWord } from '../status-labels.js'
import { FormErrors } from './FormErrors.js'

/**
 * Putting a walk-in on file. §6.2 `CUS-001`, `CUS-003`, `CUS-008`; `OFF-001a`; `LOC-005`.
 *
 * **The counter had no way to do this.** `CustomerSearch` found people and `CustomerDetail` showed
 * one, and the only writers of a customer row in the whole product were `tools/seed` and the CSV
 * importer. A shop that signed up and did not migrate a file had an empty list and no form, so a
 * prescription had no customer to belong to and the counter stopped at its first screen.
 *
 * **`CUS-003` runs before the submit, not after it.** The candidates are shown while the form is
 * open, each with an "open this record instead" button, because detection is only worth anything
 * if it can be acted on: a 409 on submit would stop the whole write queue (`OFF-001a`) and a merge
 * tool does not exist yet. The server detects again at write time and the race case, another till
 * filing the same person while this one was typing, is reported afterwards with both records
 * openable. Nothing is deleted and nothing is merged, which is what the product can honestly do.
 *
 * **Every refusal the service can give is unreachable from here**, which is the rule for a screen
 * behind a serial queue: the language and channel choosers offer only values Appendix E holds, the
 * contact kinds come from a fixed list, the date is a `date` input and is checked again here
 * because a typed `2062` is a valid date and not a birth date, and the name and first number are
 * required before the write is enqueued.
 *
 * **The three refusals are checked as the fields are left, not one at a time on submit.**
 * §16.5 asks for "inline validation on blur, never on keystroke", and this screen returned at the
 * first failure: a form missing a number and carrying a date of birth in 2062 reported the number,
 * and the person fixed it, pressed the button again and then learned about the year. Two trips
 * through a form for two things both knowable when they were typed. The summary at the top is a
 * link to each field, because on a form this long a sentence under the button is a message
 * somebody has to go hunting for.
 *
 * **Consent has three states and the third is the default.** `CUS-001` wants marketing consent per
 * channel; a checkbox cannot say "nobody asked", and a false stored for a question never put is a
 * refusal the customer never gave. So each is a chooser whose default is not asked, and the body
 * carries the field only when there is an answer.
 */

/** A customer who may already be the person being typed in. `CUS-003`. */
export interface CustomerCandidate {
  readonly id: string
  readonly name: string
  readonly matchedOn: 'name' | 'sound' | 'phone'
  readonly dateOfBirth?: string | null
}

export interface NewCustomerProps {
  /** `BR-02`: the branch the customer is filed at, which is the one being worked at. */
  readonly branchId: string
  /** Appendix K's matcher, through the shell's one read path. */
  readonly findMatches: (query: string) => Promise<readonly CustomerCandidate[]>
  /** Open an existing record instead, or the new one once it is written. */
  readonly onOpen: (customerId: string) => void
}

/**
 * The second number's kind. `CONTACT_KINDS` holds five; a landline and a WhatsApp number are the
 * two a Pakistani shop writes down beside a mobile, and `other` is not a kind anybody chooses on
 * purpose. The first number is a mobile and the third field is an email, so neither needs a chooser.
 */
const SECOND_KINDS = ['mobile', 'landline', 'whatsapp'] as const

/** Appendix E's `message_channel`, which is what `customer.preferred_channel` is checked against. */
const CHANNELS = MESSAGE_CHANNELS

type Answered = '' | 'yes' | 'no'

export function NewCustomer({ branchId, findMatches, onOpen }: NewCustomerProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [second, setSecond] = useState('')
  const [secondKind, setSecondKind] = useState<string>('mobile')
  const [email, setEmail] = useState('')
  const [address, setAddress] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [gender, setGender] = useState('')
  const [language, setLanguage] = useState('')
  const [channel, setChannel] = useState('')
  const [notes, setNotes] = useState('')
  const [tags, setTags] = useState('')
  const [marketing, setMarketing] = useState<Answered>('')
  const [recall, setRecall] = useState<Answered>('')
  const [candidates, setCandidates] = useState<readonly CustomerCandidate[]>([])
  const [raced, setRaced] = useState<readonly CustomerCandidate[] | null>(null)
  const [createdId, setCreatedId] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  /**
   * The three conditions the service refuses on, checked here so none of them reaches the queue.
   * A refused write blocks the head and everything behind it (`OFF-001a`), so a screen whose
   * refusals are reachable is a screen that can stop a shop.
   */
  const checks: readonly FieldCheck[] = [
    {
      fieldId: 'new-customer-name',
      label: t('newCustomer.nameLabel'),
      failure: name.trim() === '' ? t('newCustomer.nameRequired') : undefined,
    },
    {
      fieldId: 'new-customer-phone',
      label: t('newCustomer.phoneLabel'),
      failure: phone.trim() === '' ? t('newCustomer.phoneRequired') : undefined,
    },
    {
      fieldId: 'new-customer-dob',
      label: t('newCustomer.dobLabel'),
      // A `date` input happily accepts 2062, and `CUSTOMER_DOB_IN_FUTURE` is the server's refusal.
      failure:
        dateOfBirth !== '' && dateOfBirth > new Date().toISOString().slice(0, 10)
          ? t('newCustomer.dobInFuture')
          : undefined,
    },
  ]
  const fields = useFieldChecks(checks)

  /** §17: the message below the field it is about, with the id `aria-describedby` points at. */
  const fieldError = (fieldId: string): JSX.Element | null => {
    const message = fields.messageFor(fieldId)
    return message === undefined ? null : (
      <span id={errorId(fieldId)} className="lm-field__error">
        {message}
      </span>
    )
  }

  /**
   * `CUS-003` while the form is open, debounced for the reason `CustomerSearch` gives: this would
   * otherwise fire on every keystroke, and a Pakistani shop's uplink is the constraint the product
   * is designed around.
   *
   * Two queries rather than one, because Appendix K's signals are separate: a phone is matched
   * exactly on its last nine digits and a name by trigram and sound, and a query holding both
   * matches neither well.
   */
  useEffect(() => {
    const queries = [name.trim(), phone.trim()].filter((q) => q.length >= 2)
    if (queries.length === 0) {
      setCandidates([])
      return
    }
    let cancelled = false
    const timer = setTimeout(() => {
      void Promise.all(queries.map((q) => findMatches(q).catch(() => [])))
        .then((answers) => {
          if (cancelled) return
          const merged = new Map<string, CustomerCandidate>()
          for (const found of answers) {
            for (const candidate of found) {
              const already = merged.get(candidate.id)
              // Appendix K ranks exact phone above every name signal, so a record found both ways
              // keeps the stronger reason rather than whichever query resolved last.
              if (
                already === undefined ||
                (candidate.matchedOn === 'phone' && already.matchedOn !== 'phone')
              ) {
                merged.set(candidate.id, candidate)
              }
            }
          }
          setCandidates([...merged.values()])
        })
        .catch(() => undefined)
    }, 400)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [findMatches, name, phone])

  useWriteSettled(snapshot, writeId, (write) => {
    setWriteId(null)
    const answer = (
      typeof write.response === 'object' && write.response !== null ? write.response : {}
    ) as {
      created?: { id?: unknown }
      duplicates?: unknown
    }
    const id = typeof answer.created?.id === 'string' ? answer.created.id : null
    setCreatedId(id)
    // The server's own detection, which ran inside the write. Anything in it that the form had not
    // already shown is a record another till filed while this one was being typed.
    const seen = new Set(candidates.map((c) => c.id))
    const fresh = (Array.isArray(answer.duplicates) ? answer.duplicates : [])
      .filter((d): d is CustomerCandidate => typeof (d as CustomerCandidate)?.id === 'string')
      .filter((d) => !seen.has(d.id))
    setRaced(fresh.length > 0 ? fresh : null)
  })

  const submit = useCallback(async () => {
    // Every failure at once. Submit is the one moment somebody has asked about the whole form
    // rather than about the field they are standing in.
    if (!fields.ready()) return
    const typedName = name.trim()

    setError(null)
    const contacts = [
      { kind: 'mobile', value: phone.trim() },
      ...(second.trim() === '' ? [] : [{ kind: secondKind, value: second.trim() }]),
      ...(email.trim() === '' ? [] : [{ kind: 'email', value: email.trim() }]),
    ]
    const tagList = tags
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag !== '')

    const write = (await enqueue({
      method: 'POST',
      path: '/customers',
      label: t('newCustomer.queueLabel', { name: typedName }),
      body: {
        name: typedName,
        contacts,
        branchId,
        ...(address.trim() === '' ? {} : { address: address.trim() }),
        ...(notes.trim() === '' ? {} : { notes: notes.trim() }),
        ...(dateOfBirth === '' ? {} : { dateOfBirth }),
        ...(gender.trim() === '' ? {} : { gender: gender.trim() }),
        ...(language === '' ? {} : { preferredLanguage: language }),
        ...(channel === '' ? {} : { preferredChannel: channel }),
        ...(tagList.length === 0 ? {} : { tags: tagList }),
        // Absent when nobody asked, which is not the same as a recorded no.
        ...(marketing === '' ? {} : { marketingConsent: marketing === 'yes' }),
        ...(recall === '' ? {} : { recallConsent: recall === 'yes' }),
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [
    address,
    branchId,
    channel,
    dateOfBirth,
    email,
    enqueue,
    gender,
    language,
    marketing,
    name,
    notes,
    phone,
    recall,
    second,
    secondKind,
    t,
    tags,
  ])

  const line = statusLine(t, snapshot, writeId, t('queue.subject.customer'))

  const why = (candidate: CustomerCandidate): string =>
    t(
      candidate.matchedOn === 'phone'
        ? 'newCustomer.foundByPhone'
        : candidate.matchedOn === 'sound'
          ? 'newCustomer.foundBySound'
          : 'newCustomer.foundByName',
    )

  const candidateList = (
    heading: string,
    rows: readonly CustomerCandidate[],
    hint: string,
  ): JSX.Element => (
    <div className="lm-new-customer__matches">
      <h3 className="lm-new-customer__matches-heading">{heading}</h3>
      <p className="lm-hint">{hint}</p>
      <ul className="lm-matches">
        {rows.map((candidate) => (
          <li key={candidate.id}>
            <button
              type="button"
              className="lm-match"
              onClick={() => {
                onOpen(candidate.id)
              }}
            >
              {/* `LOC-005`: a name takes its own direction inside whichever page it sits in. */}
              <span className="lm-match__name" dir="auto">
                {candidate.name}
              </span>
              <span className="lm-match__why">
                {why(candidate)}
                {candidate.dateOfBirth === null || candidate.dateOfBirth === undefined
                  ? ''
                  : ` · ${t('newCustomer.bornOn')} `}
                {candidate.dateOfBirth === null || candidate.dateOfBirth === undefined ? null : (
                  <Ltr>{candidate.dateOfBirth}</Ltr>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )

  if (createdId !== null) {
    return (
      <section className="lm-new-customer" aria-labelledby="new-customer-done">
        <h2 id="new-customer-done" className="lm-new-customer__heading">
          {t('newCustomer.doneHeading')}
        </h2>
        <p className="lm-new-customer__effects">{t('newCustomer.done')}</p>
        {raced === null
          ? null
          : candidateList(
              t('newCustomer.racedHeading'),
              raced,
              // No merge tool exists, so the sentence says what the shop can actually do: decide
              // which record to carry on with. `CUS-003`'s merge is audited and reversible and is
              // its own piece of work; pointing at it now would name a screen that is not there.
              t('newCustomer.raced'),
            )}
        <button
          type="button"
          className="lm-button"
          onClick={() => {
            onOpen(createdId)
          }}
        >
          {t('newCustomer.openTheNew')}
        </button>
      </section>
    )
  }

  return (
    <section className="lm-new-customer" aria-labelledby="new-customer-heading">
      <h2 id="new-customer-heading" className="lm-new-customer__heading">
        {t('newCustomer.heading')}
      </h2>
      <p className="lm-new-customer__effects">{t('newCustomer.whyPhone')}</p>

      {/* At the top and each entry a link, because on a form that scrolls a sentence under the
          button is a message about a field somebody cannot see. */}
      <FormErrors errors={fields.summary} />

      <div className="lm-field">
        {/* Explicitly associated rather than wrapping the input, because the message below is
            inside this element: a wrapping label takes its accessible name from everything it
            contains, so the field would be called "Name, as they say it A customer needs a name."
            to a screen reader and to anything else that reads an accessible name. */}
        <label className="lm-field__label" htmlFor="new-customer-name">
          {t('newCustomer.nameLabel')}
        </label>
        {/* `LOC-005`: the field follows what is typed, so an Urdu name is not reordered inside an
            English page. The same `dir="auto"` `CustomerSearch` uses on the same value. */}
        <input
          id="new-customer-name"
          className="lm-input"
          dir="auto"
          value={name}
          autoComplete="off"
          onChange={(e) => {
            fields.edit('new-customer-name')
            setName(e.currentTarget.value)
          }}
          {...fields.inputProps('new-customer-name')}
        />
        {fieldError('new-customer-name')}
      </div>

      <div className="lm-field">
        <label className="lm-field__label" htmlFor="new-customer-phone">
          {t('newCustomer.phoneLabel')}
        </label>
        <input
          id="new-customer-phone"
          className="lm-input"
          type="tel"
          inputMode="tel"
          value={phone}
          autoComplete="off"
          onChange={(e) => {
            fields.edit('new-customer-phone')
            setPhone(e.currentTarget.value)
          }}
          {...fields.inputProps('new-customer-phone')}
        />
        {fieldError('new-customer-phone')}
      </div>

      {candidates.length === 0
        ? null
        : candidateList(
            t('newCustomer.alreadyHeading'),
            candidates,
            t('newCustomer.alreadyHint'),
          )}

      <div className="lm-new-customer__pair">
        <label className="lm-field">
          <span className="lm-field__label">{t('newCustomer.secondLabel')}</span>
          <input
            className="lm-input"
            type="tel"
            inputMode="tel"
            value={second}
            autoComplete="off"
            onChange={(e) => setSecond(e.currentTarget.value)}
          />
        </label>
        <label className="lm-field">
          <span className="lm-field__label">{t('newCustomer.secondKindLabel')}</span>
          <select
            className="lm-input"
            value={secondKind}
            onChange={(e) => setSecondKind(e.currentTarget.value)}
          >
            {SECOND_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {t(
                  kind === 'mobile'
                    ? 'newCustomer.kindMobile'
                    : kind === 'landline'
                      ? 'newCustomer.kindLandline'
                      : 'newCustomer.kindWhatsapp',
                )}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="lm-field">
        <span className="lm-field__label">{t('newCustomer.emailLabel')}</span>
        <input
          className="lm-input"
          type="email"
          dir="ltr"
          value={email}
          autoComplete="off"
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('newCustomer.addressLabel')}</span>
        <input
          className="lm-input"
          dir="auto"
          value={address}
          autoComplete="off"
          onChange={(e) => setAddress(e.currentTarget.value)}
        />
      </label>

      <div className="lm-new-customer__pair">
        <div className="lm-field">
          <label className="lm-field__label" htmlFor="new-customer-dob">
            {t('newCustomer.dobLabel')}
          </label>
          {/* Western digits in both languages (`D-03`), which is what a `date` input gives. */}
          <input
            id="new-customer-dob"
            className="lm-input"
            type="date"
            value={dateOfBirth}
            onChange={(e) => {
              fields.edit('new-customer-dob')
              setDateOfBirth(e.currentTarget.value)
            }}
            {...fields.inputProps('new-customer-dob')}
          />
          {fieldError('new-customer-dob')}
        </div>
        <label className="lm-field">
          {/* `CUS-001`: "gender where legally collectable". Free text and never a chooser, because
              Appendix E registers no gender block and inventing one would fix a value set the spec
              declined to fix. */}
          <span className="lm-field__label">{t('newCustomer.genderLabel')}</span>
          <input
            className="lm-input"
            dir="auto"
            value={gender}
            autoComplete="off"
            onChange={(e) => setGender(e.currentTarget.value)}
          />
        </label>
      </div>

      <div className="lm-new-customer__pair">
        <label className="lm-field">
          <span className="lm-field__label">{t('newCustomer.languageLabel')}</span>
          <select
            className="lm-input"
            value={language}
            onChange={(e) => setLanguage(e.currentTarget.value)}
          >
            <option value="">{t('newCustomer.languageUnset')}</option>
            {LOCALES.map((locale) => (
              <option key={locale} value={locale}>
                {t(locale === 'ur' ? 'newCustomer.languageUrdu' : 'newCustomer.languageEnglish')}
              </option>
            ))}
          </select>
        </label>
        <label className="lm-field">
          <span className="lm-field__label">{t('newCustomer.channelLabel')}</span>
          <select
            className="lm-input"
            value={channel}
            onChange={(e) => setChannel(e.currentTarget.value)}
          >
            <option value="">{t('newCustomer.channelUnset')}</option>
            {CHANNELS.map((option) => (
              <option key={option} value={option}>
                {/* The chase table, because both read Appendix E's `message_channel` block and one
                    vocabulary for one enumeration is the point of `status-labels.ts`. All five are
                    offered rather than `MSG-001`'s two: this records what the customer prefers,
                    which is a fact about them and not a promise about what v1 can send. */}
                {chaseChannelWord(t, option)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="lm-hint">{t('newCustomer.channelSendableNote')}</p>

      <div className="lm-new-customer__pair">
        <label className="lm-field">
          <span className="lm-field__label">{t('newCustomer.marketingLabel')}</span>
          <select
            className="lm-input"
            value={marketing}
            onChange={(e) => setMarketing(e.currentTarget.value as Answered)}
          >
            <option value="">{t('newCustomer.consentUnasked')}</option>
            <option value="yes">{t('newCustomer.consentYes')}</option>
            <option value="no">{t('newCustomer.consentNo')}</option>
          </select>
        </label>
        <label className="lm-field">
          <span className="lm-field__label">{t('newCustomer.recallLabel')}</span>
          <select
            className="lm-input"
            value={recall}
            onChange={(e) => setRecall(e.currentTarget.value as Answered)}
          >
            <option value="">{t('newCustomer.consentUnasked')}</option>
            <option value="yes">{t('newCustomer.consentYes')}</option>
            <option value="no">{t('newCustomer.consentNo')}</option>
          </select>
        </label>
      </div>
      <p className="lm-hint">{t('newCustomer.consentMeaning')}</p>

      <label className="lm-field">
        <span className="lm-field__label">{t('newCustomer.tagsLabel')}</span>
        <input
          className="lm-input"
          dir="auto"
          value={tags}
          autoComplete="off"
          onChange={(e) => setTags(e.currentTarget.value)}
        />
      </label>

      {/* `CUS-008` `MUST`: the warning is visible, not a tooltip, and it sits above the box rather
          than under it, because a warning read after typing has already failed. A sibling of the
          label rather than a child, so it does not become part of the field's accessible name. */}
      <p className="lm-field__warning">{t('newCustomer.notesWarning')}</p>
      <label className="lm-field">
        <span className="lm-field__label">{t('newCustomer.notesLabel')}</span>
        <textarea
          className="lm-input lm-input--area"
          dir="auto"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />
      </label>

      {error === null ? null : <p className="lm-error">{error}</p>}
      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      <button
        type="button"
        className="lm-button lm-button--primary"
        disabled={writeId !== null}
        onClick={() => {
          void submit()
        }}
      >
        {t('newCustomer.submit')}
      </button>
    </section>
  )
}
